// Verwaltet hochgeladene Anhänge lokal + reaktiv.
// Lokale Uploads bekommen eine Blob-URL für die Vorschau.
// Server-Dateien können später per Status-Polling in denselben Store geschrieben werden.

/**
 * @file Dient als Zwischenschicht für Datei-Uploads.
 * - Verwaltet einen globalen Zustand für alle laufenden Uploads.
 * - Ermöglicht es verschiedenen UI-Komponenten, den Status desselben Uploads zu teilen.
 * - Speichert den Fortschritt lokal, um ihn über Seiten-Neuladungen hinweg (theoretisch) beizubehalten.
 */

import { uploadHugeFile } from './files.js'
import { resolveApiUrl } from './api.js'

const GLOBAL_KEY = '__LOCAL_FILES_STATE__'

function getStore() {
  if (!window[GLOBAL_KEY]) {
    window[GLOBAL_KEY] = {
      files: new Map(),
      listeners: new Map()
    }
  }
  return window[GLOBAL_KEY]
}

function cloneState(state) {
  return state ? { ...state } : null
}

function emit(fileRef) {
  const store = getStore()
  const listeners = store.listeners.get(String(fileRef || ''))
  if (!listeners) return

  const current = cloneState(store.files.get(String(fileRef || '')))
  for (const fn of listeners) fn(current)
}

function mergeRanges(ranges) {
  const items = (ranges || [])
    .map((r) => [
      Math.max(0, Number(r?.start ?? r?.[0] ?? 0)),
      Math.max(0, Number(r?.end ?? r?.[1] ?? -1))
    ])
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e >= s)
    .sort((a, b) => a[0] - b[0])

  const merged = []
  for (const [start, end] of items) {
    const last = merged[merged.length - 1]
    if (!last || start > last[1] + 1) merged.push([start, end])
    else last[1] = Math.max(last[1], end)
  }
  return merged
}

function uploadedBytesFromRanges(ranges) {
  return mergeRanges(ranges).reduce((sum, [start, end]) => sum + (end - start + 1), 0)
}

function isFinishedState(state) {
  if (!state) return false
  return Boolean(
    state.finished ||
      state.status === 'done' ||
      state.status === 'finished' ||
      Number(state.progress) >= 100
  )
}

function setState(fileRef, patch) {
  const store = getStore()
  const key = String(fileRef || '')
  const prev = store.files.get(key) || {}
  const next = {
    ...prev,
    ...patch,
    fileRef: key,
    updatedAt: Date.now()
  }

  store.files.set(key, next)
  emit(key)
  return next
}

export function getFileStatus(fileRef) {
  const store = getStore()
  return cloneState(store.files.get(String(fileRef || '')))
}

export function subscribeFileStatus(fileRef, cb) {
  const store = getStore()
  const key = String(fileRef || '')

  if (!store.listeners.has(key)) store.listeners.set(key, new Set())
  store.listeners.get(key).add(cb)

  cb(cloneState(store.files.get(key)))

  return () => {
    store.listeners.get(key)?.delete(cb)
  }
}

export function updateFileStatus(fileRef, patch) {
  return setState(fileRef, patch)
}

export function isFileFinished(state) {
  return isFinishedState(state)
}

export function applyServerFileStatus(fileRef, payload) {
  const record = payload?.answer?.record || payload?.record || payload?.answer || payload || null
  if (!record) return null

  const existing = getFileStatus(fileRef)

  const size =
    Number(record.total_size_bytes ?? record.size_bytes ?? record.total_size ?? record.size) ||
    existing?.total ||
    0

  const uploaded = uploadedBytesFromRanges(record.received_ranges || [])
  const progressFromServer = Number(record.progress)

  const progress = Number.isFinite(progressFromServer)
    ? Math.max(0, Math.min(100, Math.round(progressFromServer)))
    : size > 0
      ? Math.max(0, Math.min(100, Math.round((uploaded / size) * 100)))
      : existing?.progress || 0

  const finished = Boolean(
    record.finished ||
      record.complete ||
      record.closed ||
      progress >= 100
  )

  return setState(String(fileRef || ''), {
    ...(existing || {}),
    source: existing?.source || 'remote',
    status: finished ? 'done' : (record.status || 'uploading'),
    progress,
    uploaded,
    total: size,
    name: record.original_filename || record.filename || existing?.name || null,
    type: record.mime_type || existing?.type || null,
    previewUrl: finished
      ? resolveApiUrl(`/users/files/${encodeURIComponent(String(fileRef || ''))}`)
      : resolveApiUrl(`/users/files/parts/live/${encodeURIComponent(String(fileRef || ''))}`),
    finished
  })
}

export function uploadLocalFile(file) {
  const store = getStore()

  const tempId =
    crypto.randomUUID?.() ||
    String(Date.now() + Math.random())

  const previewUrl = URL.createObjectURL(file)

  store.files.set(tempId, {
    fileRef: null,
    source: 'local',
    file,
    previewUrl,
    progress: 0,
    uploaded: 0,
    total: file.size,
    status: 'init',
    finished: false,
    name: file.name,
    type: file.type,
    updatedAt: Date.now()
  })

  let resolveFileRef
  const fileRefPromise = new Promise((resolve) => {
    resolveFileRef = resolve
  })

  uploadHugeFile(file, {
    onReady: ({ fileRef }) => {
      const entry = getFileStatus(tempId)
      if (!entry) return

      const next = {
        ...entry,
        fileRef,
        source: 'local',
        status: 'uploading',
        updatedAt: Date.now()
      }

      store.files.set(String(fileRef), next)
      store.files.delete(tempId)

      resolveFileRef(fileRef)
      emit(fileRef)
    },

    onProgress: (p) => {
      const fileRef = p.fileRef
      if (!fileRef) return

      const entry = getFileStatus(fileRef)
      if (!entry) return

      setState(fileRef, {
        ...entry,
        source: 'local',
        progress: Math.max(0, Math.min(100, Number(p.percent) || 0)),
        uploaded: Number(p.current ?? entry.uploaded ?? 0),
        total: Number(p.total ?? entry.total ?? file.size),
        status: 'uploading',
        finished: false
      })
    },

    onDone: (res) => {
      const fileRef = res.fileRef
      const entry = getFileStatus(fileRef)
      if (!entry) return

      setState(fileRef, {
        ...entry,
        source: 'local',
        status: 'done',
        progress: 100,
        uploaded: entry.total ?? file.size,
        total: entry.total ?? file.size,
        finished: true
      })
    },

    onError: (err) => {
      const entry = getFileStatus(tempId)
      if (entry) {
        setState(tempId, {
          ...entry,
          source: 'local',
          status: 'error',
          error: err?.message || String(err),
          finished: false
        })
      }
    }
  })

  return fileRefPromise
}